import React, { useEffect, useState } from 'react';
import { AnyAction } from 'redux';

import { Button } from '../../button/button';
import { BUTTON_SIZE } from '../../button/types';
import { UI_KIT_TEXT_TYPOGRAPHY } from '../../constants';
import { Image } from '../../image/image';
import { MarkdownExtXContent } from '../../markdown/markdown';
import { SimpleText } from '../../simple-text/simple-text';
import Spin from '../../spin/spin';
import { StudentModal } from '../../student-modal/student-modal';
import Text from '../../text/text';
import { useRobotSelector } from '../context';
import { getIsReadOnly } from '../selectors';
import { actions } from '../useRobotRun';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import styles from './robot__code-light.module.scss';

interface Props {
    dispatch: React.Dispatch<AnyAction>;
    showOnboarding: boolean;
    description?: string;
    src?: string;
    autoshow?: boolean;
}

const BUTTON_DISABLED_DELAY = 3000;

const Onboarding = ({ dispatch, showOnboarding, description, src, autoshow }: Props) => {
    const [isSrcLoaded, setSrcLoaded] = useState(false);
    const [buttonDisabled, setButtonDisabled] = useState(true);

    useEffect(() => {
        if (autoshow) {
            if (showOnboarding && buttonDisabled) {
                const timerId = setTimeout(() => {
                    setButtonDisabled(false);
                }, BUTTON_DISABLED_DELAY);

                return () => {
                    clearTimeout(timerId);
                };
            }
        } else {
            setButtonDisabled(false);
        }
        return () => {};
    }, [showOnboarding, autoshow]);

    useEffect(() => {
        if (autoshow) {
            dispatch(actions.showOnboarding({ showOnboarding: true }));
        }
    }, [autoshow, dispatch]);

    const handleClose = () => {
        dispatch(actions.showOnboarding({ showOnboarding: false }));
    };

    const PreloadImage = () => (
        <Image
            alt="onboarding"
            onLoad={() => {
                setSrcLoaded(true);
            }}
            src={src}
            style={{ visibility: 'hidden' }}
        />
    );

    return (
        <>
            <PreloadImage />
            <StudentModal mix={styles.onboarding} opened={showOnboarding} steeperRadius={true}>
                <div className={styles.onboarding__content}>
                    {description && (
                        <Text
                            className={styles.onboarding__description}
                            typography={UI_KIT_TEXT_TYPOGRAPHY.headlineXSMedium}
                        >
                            <MarkdownExtXContent>{description}</MarkdownExtXContent>
                        </Text>
                    )}
                    <div className={styles.onboarding__gif_container}>
                        {/* Первые кадры GIFки грузятся быстрее, чем произойдет isSrcLoaded (кроме Safari), и они перекроют spin */}
                        <Image alt="onboarding" className={styles.onboarding__gif} src={src} />
                        {!isSrcLoaded && <Spin center={true} />}
                    </div>
                    <Button
                        className={styles.onboarding__button}
                        disabled={buttonDisabled}
                        onClick={handleClose}
                        size={BUTTON_SIZE.L}
                        view={Button.VIEW.ACTION}
                    >
                        <SimpleText id="roverV2.modal.onboarding.apply" />
                    </Button>
                </div>
            </StudentModal>
        </>
    );
};

export const OnboardingMemo = React.memo(Onboarding);

const RobotOnboarding = () => {
    const problem = useRobotSelector((s) => s.problem);
    const dispatch = useRobotSelector((s) => s.dispatch);
    const showOnboarding = useRobotSelector((s) => s.state.showOnboarding);
    const isReadOnly = useRobotSelector(getIsReadOnly);

    const onboarding = problem?.onboardings?.[0];

    if (isReadOnly || !onboarding?.src) {
        return null;
    }

    return (
        <OnboardingMemo
            autoshow={onboarding?.autoshow}
            description={onboarding?.description}
            dispatch={dispatch}
            showOnboarding={showOnboarding}
            src={onboarding?.src}
        />
    );
};

export default RobotOnboarding;
